import { useCallback, useMemo } from 'react';
import { NavLink, withRouter } from 'react-router-dom';
import ROUTE from '../../../../constants/route';
import { Option, Role } from '../../../../helpers/user';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import ProfileActions from '../../../../pages/CandidateProfileOwn/actions';
import AccessLimitsAlert from '../../../../scenes/Dialog/components/AccessLimitsAlert';
import BasicDialog from '../../../../scenes/Dialog/components/BasicDialog';
import BuyCreditsDialog from '../../../../scenes/Dialog/components/BuyCreditsDialog';
import CandidateProfileNotApproved from '../../../../scenes/Dialog/components/CandidateProfileNotApproved';
import ConfirmYourProfile from '../../../../scenes/Dialog/components/ConfirmYourProfile';
import CreateProfileDialog from '../../../../scenes/Dialog/components/CreateProfileDialog';
import NoAddReviewRight from '../../../../scenes/Dialog/components/NoAddReviewRight';
import UpgradeRecruiterAccount from '../../../../scenes/Dialog/components/UpgradeRecruiterAccount';
import { CLICK_HANDLERS, ERRORS } from '../../../../scenes/Header/components/MainNavigation/schema';
import getMainMenuSchema from '../schema';
import './styles.css';

const Navigation = ({ user, location, history }) => {
  const lg = useMediaQuery('lg');
  const menu = useMemo(() => {
    let items = getMainMenuSchema(user, location.pathname);

    items = items.map(item => {
      let children = item.children || [];

      if (!lg && !!children.length) {
        children.unshift({
          ...item,
          children: [],
        });
      }

      return {
        ...item,
        disabled: item.disabled  ? item.disabled : !lg && !!children.length,
      };
    });

    return items;
  }, [
    location.pathname,
    user,
    lg
  ]);

  const [
    isRecruiter,
    hasCandidateAccount,
    isCandidateEmailConfirm,
  ] = useMemo(
    () => [
      Role.isRecruiter(user),
      Option.hasCandidateAccount(user.options),
      Option.isCandidateEmailConfirm(user.options),
    ],
    [user]
  );

  const handleAddPosition = useCallback(() => {
    ProfileActions.addPosition(
      {
        type: 'position',
        isMyProfile: true,
        isRecruiter,
      },
    ).then(() => {
      if (location.pathname !== ROUTE.CANDIDATE_PROFILE) {
        history.push(ROUTE.CANDIDATE_PROFILE);
      } else {
        history.push('/');
        history.replace(ROUTE.CANDIDATE_PROFILE);
      }
    });
  }, [history, isRecruiter, location.pathname]);

  const onClickAccessControl = useCallback((e, item) => {
    console.log(item.disabled)
    if (item.disabled) {
      e.preventDefault();
      if (item.disabled.reason === ERRORS.UPGRADE_RECRUITER_ACCOUNT) {
        UpgradeRecruiterAccount();
      } else if (item.disabled.reason === ERRORS.BECOME_CANDIDATE) {
        if (!hasCandidateAccount) {
          CreateProfileDialog();
        } else if (hasCandidateAccount && !isCandidateEmailConfirm) {
          ConfirmYourProfile();
        } else {
          CandidateProfileNotApproved();
        }
      } else if (item.disabled.reason === ERRORS.ACCESS_LIMITS) {
        AccessLimitsAlert();
      } else if (item.disabled.reason === ERRORS.NO_ADD_REVIEW_RIGHT) {
        NoAddReviewRight({ type: item.disabled.type });
      } else if (item.disabled.reason === ERRORS.PURCHASE_MORE_CREDITS) {
        BuyCreditsDialog({ history });
      } else if (item.disabled.reason === ERRORS.MANAGE_EMAILS_TEMPLATES) {
        BasicDialog({
          bodyRender:
            'You cannot access Manage Emails Templates if you are not allowed to access Manage Process and Manage Projects pages',
        });
      }
    } else if (item.onClickHandler) {
      e.preventDefault();

      if (item.onClickHandler === CLICK_HANDLERS.ADD_CANDIDATE_POSITION) {
        handleAddPosition();
      }
    }
  }, [handleAddPosition, hasCandidateAccount, history, isCandidateEmailConfirm]);

  const menuItemRender = useCallback(item => {
    if (item.disabled || !item.href) {
      return (
        <span
          tabIndex={0}
          className={'navigation__item navigation__item--disabled'}
          onClick={e => lg && onClickAccessControl(e, item)}
        >
          {item.name}

          {!!item?.children?.length && (
            <svg width="20" height="20" fill="none">
              <path d="m15.83 7.5-5.83 5-5.83-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </span>
      )
    }

    return (
      <NavLink
        className={'navigation__item'}
        exact={item.exact}
        isActive={() => item.isActive || (lg && item.isChildActive)}
        onClick={e => onClickAccessControl(e, item)}
        to={item.href}
      >
        {item.name}

        {!!item?.children?.length && (
          <svg width="20" height="20" fill="none">
            <path d="m15.83 7.5-5.83 5-5.83-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </NavLink>
    );
  }, [lg, onClickAccessControl]);

  const subMenuRender = useCallback((item, index) => {
    if (!item?.children?.length) {
      return null;
    }

    return (
      <ul className={'navigation__sub-list'}>
        {item.children.map((child, childIndex) => {
          if (!child.visible) {
            return null;
          }

          return (
            <li
              key={`${index}-${childIndex}`}
              className={'navigation__sub-list-item'}
            >
              {menuItemRender(child)}
            </li>
          );
        })}
      </ul>
    );
  }, [menuItemRender]);

  return (
    <nav className={'navigation'}>
      <ul className={'navigation__list'}>
        {menu.map((item, index) => {
          if (!item.visible) {
            return null;
          }

          if (!lg && !!item?.children?.length) {
            return (
              <li key={index} className={'navigation__list-item'}>
                <details open={item.isActive || item.isChildActive}>
                  <summary>
                    {menuItemRender(item)}
                  </summary>
                  {subMenuRender(item, index)}
                </details>
              </li>
            );
          }

          return (
            <li key={index} className={'navigation__list-item'}>
              {menuItemRender(item)}
              {subMenuRender(item, index)}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default withRouter(Navigation);
